import React, { useEffect } from 'react'
import './App.css'
import { Router, handleScale, strings } from './lib'
import { DepotoCore, ENDPOINT, getEndpoint, NotificationEvent } from 'depoto-core'
import { StoreProvider } from './store'
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query'
import 'react-toastify/dist/ReactToastify.css'
import { toast, ToastContainer } from 'react-toastify'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import * as Sentry from '@sentry/react'
import { GQLProvider } from './context/GQLClientProvider'
import { toastWarning, toastInfo, toastError, CloseToastButton, ToastCloseAllButton } from './lib/toastHelpers'
import { BarcodeProvider } from './contexts/BarcodeContext'
import { BasicEntitiesProvider } from './context/BasicEntitiesContext'
import { SettingsProvider } from './contexts/SettingsContext'
import { NetInfoProvider } from './contexts/NetInfoContext'
import { NetworkStatusBar } from './components/NetworkStatusBar'
import { UnpaidInvoicesWarning } from './components/UnpaidInvoicesWarning'
import { ErrorBoundary } from 'react-error-boundary'
import { faHeartBroken } from '@fortawesome/pro-duotone-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import DepotoLogo from './assets/img/group559.svg?react'
import { PostHogErrorBoundary, PostHogProvider } from 'posthog-js/react'
import posthog from 'posthog-js'
import { TooltipProvider } from './components/shadcn/tooltip'
import './lib/errorToastRegistry'

const options = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
  defaults: '2025-05-24',
}

declare global {
  interface Window {
    _depoto: any
    depotoEnv?: 'prod' | 'stage' | 'dev' | 'custom'
    lastRoute: string
    batchGroupStatus: { [groupId: string]: { status: any; errors: any } }
    weightHistory: string[]
  }
}

const queryClient = new QueryClient()
const core: DepotoCore = new DepotoCore('depoto-expeditions-v2')

const App = () => {
  useEffect(() => {
    document.title = strings.appName
    handleScale()
    window.lastRoute = window.location.pathname !== '/login' ? window.location.pathname : '/'
    core.services.baseEvent.subscribe().then((notificationEvent: NotificationEvent) => {
      if (
        notificationEvent.operation === 'update' &&
        notificationEvent.method === 'updateOrder' &&
        notificationEvent.type === 'resolve'
      ) {
        if (notificationEvent.errors.length > 0) {
          toastWarning(notificationEvent.errors.join(', '))
        } else {
          // const orderId = notificationEvent?.response?.data?.updateOrder?.data?.id // todo reservationNumber
          const orderId = ''
          // toastInfo(`Objednávka ${orderId} uložena`)
        }
      }
    })
    core.services.baseEvent.subscribeToErrors().then(e => {
      console.log({ e })
      const errors = (e as (string | { message: string })[])
        .filter(err => typeof err === 'string' || typeof err.message === 'string')
        .map(err => (typeof err === 'string' ? err : err.message || 'Unknown error'))

      if (Array.isArray(errors) && errors.length > 0) {
        if (!errors[0]?.includes('Anonym')) {
          toastWarning(errors.join(', '))
        }
      } else if (e?.status === 500) {
        toastWarning('Chyba: error 500')
      }
    })
  }, [])

  function fallbackRender({ error }) {
    return (
      <div className="flex flex-col items-center justify-between min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">
        <div className="flex flex-1 flex-row items-center justify-center">
          <DepotoLogo width="300" height="100" />
        </div>

        <div className="bg-white flex-2 rounded-2xl shadow-2xl p-12 max-w-2xl w-full text-center border border-gray-200">
          <div className="mb-8">
            <FontAwesomeIcon icon={faHeartBroken} className="text-red-500 drop-shadow-lg animate-pulse" size={'6x'} />
          </div>

          <h1 className="text-3xl font-bold text-gray-800 mb-4">Něco se pokazilo</h1>

          <p className="text-gray-600 mb-8 text-lg leading-relaxed">
            Aplikace narazila na neočekávanou chybu. Zkuste prosím obnovit stránku. O chybě už víme a pracujeme na její
            opravě.
          </p>

          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
            <h3 className="text-red-800 font-semibold mb-3 text-left">Detaily chyby:</h3>
            <pre className="text-red-700 text-sm text-left whitespace-pre-wrap break-words font-mono bg-red-100 p-4 rounded border overflow-auto max-h-60">
              <b>{error.message}</b> <br /> {error.stack}
            </pre>
          </div>

          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
            Obnovit stránku
          </button>
        </div>
        <div className="flex flex-1 flex-row items-center justify-center"></div>
      </div>
    )
  }

  return (
    <div className="App">
      <PostHogProvider apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY} options={options}>
        <PostHogErrorBoundary fallback={fallbackRender}>
          <QueryClientProvider client={queryClient}>
            <ReactQueryDevtools />
            <StoreProvider>
              <BarcodeProvider>
                <NetInfoProvider>
                  <GQLProvider>
                    <BasicEntitiesProvider>
                      <SettingsProvider>
                        <TooltipProvider>
                          <Router core={core} />
                          <NetworkStatusBar />
                          <UnpaidInvoicesWarning />
                        </TooltipProvider>
                      </SettingsProvider>
                    </BasicEntitiesProvider>
                  </GQLProvider>
                </NetInfoProvider>
              </BarcodeProvider>
            </StoreProvider>
          </QueryClientProvider>
          <ToastContainer position={'bottom-left'} closeButton={CloseToastButton} limit={10} />
          <ToastCloseAllButton />
        </PostHogErrorBoundary>
      </PostHogProvider>
    </div>
  )
}

export default App
